import React from "react";
import { connect } from "react-redux";
// @material-ui/core components
import withStyles from "@material-ui/core/styles/withStyles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import CircularProgress from "@material-ui/core/CircularProgress";
// @material-ui/icons
import Close from "@material-ui/icons/Close";
// core components
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";

import modalStyle from "assets/jss/material-dashboard-pro-react/modalStyle.jsx";
import { enrichMediaFromUrl } from "../media-library-api.js";

// The fields video-api enforces on commit (and returns in a 422
// { mandatoryMissing }). Save stays disabled while any is empty.
const MANDATORY = ["title", "description", "copyright_notice", "credit_line", "source_url"];

// The editable field set the dialog always renders. `key` maps to the flat
// `fields` map from analyze; `required` marks the four gating fields; `area`
// renders a textarea; `optional` fields only appear when analyze returned them.
const FIELD_DEFS = [
  { key: "title", label: "Title", required: true },
  { key: "description", label: "Description", required: true, area: true },
  { key: "file_name", label: "File name" },
  { key: "copyright_notice", label: "Copyright notice", required: true },
  { key: "credit_line", label: "Credit line", required: true },
  { key: "source_url", label: "Source URL", required: true },
  { key: "source", label: "Source" },
  { key: "uploaded_by", label: "Uploaded by" },
  { key: "photographer", label: "Photographer", optional: true },
  { key: "keywords", label: "Keywords", optional: true },
  { key: "city", label: "City", optional: true },
  { key: "country", label: "Country", optional: true },
  { key: "captured_at", label: "Captured at", optional: true }
];

function isBlank(v) {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim() === "";
  return false;
}

// Media preview from the analyze `preview` descriptor. `preview` may be a bare
// URL string or an object with { url, type } — handle both defensively.
function Preview(props) {
  const preview = props.preview;
  const mediaType = props.mediaType;
  if (!preview) return null;
  // Backend analyze preview descriptor (video-api preview.js): images →
  // { type:'image', imageUrl }, video → { type:'video', posterUrl }. Read those
  // keys (url/src were never emitted, so no preview ever rendered).
  const url =
    typeof preview === "string"
      ? preview
      : preview.imageUrl || preview.posterUrl || preview.url || preview.src;
  if (!url) return null;
  const kind = (preview && preview.type) || mediaType || "image";

  const boxStyle = { textAlign: "center", marginBottom: 16 };
  if (kind === "video") {
    return (
      <div style={boxStyle}>
        <video
          src={url}
          controls
          style={{ maxWidth: "100%", maxHeight: 280 }}
        />
      </div>
    );
  }
  if (kind === "audio") {
    return (
      <div style={boxStyle}>
        <audio src={url} controls style={{ width: "100%" }} />
      </div>
    );
  }
  return (
    <div style={boxStyle}>
      <img
        src={url}
        alt="preview"
        style={{ maxWidth: "100%", maxHeight: 280, objectFit: "contain" }}
      />
    </div>
  );
}

class ReviewDialog extends React.Component {
  constructor(props) {
    super(props);
    // Seed the editable form from the flat `fields` map. Keys may be missing;
    // default to "" so every rendered input is controlled.
    const fields =
      props.analysis && props.analysis.fields ? props.analysis.fields : {};
    const form = {};
    FIELD_DEFS.forEach(function(def) {
      const v = fields[def.key];
      // keywords may come back as an array — flatten to a comma list for editing.
      form[def.key] = Array.isArray(v)
        ? v.join(", ")
        : v === null || v === undefined
          ? ""
          : String(v);
    });
    this.state = {
      form: form,
      // mandatoryMissing highlighted after a 422 from commit.
      serverMissing: [],
      fetching: false
    };
  }

  // Scrape the entered Source URL (Getty/AP article/etc.) and fill blank fields
  // only — never clobber what the file or the user already provided.
  handleFetchUrl = () => {
    const self = this;
    const url = (this.state.form.source_url || "").trim();
    if (!url || this.state.fetching) return;
    this.setState({ fetching: true });
    enrichMediaFromUrl(this.props.activeDomain, url, this.state.form.source || undefined)
      .then(function(res) {
        const fields = (res && res.fields) || {};
        self.setState(function(prev) {
          const form = Object.assign({}, prev.form);
          Object.keys(fields).forEach(function(k) {
            const v = fields[k];
            if (v && !(form[k] && String(form[k]).trim())) form[k] = String(v);
          });
          const nextMissing = prev.serverMissing.filter(function(k) {
            return !(form[k] && String(form[k]).trim());
          });
          return { form: form, serverMissing: nextMissing, fetching: false };
        });
      })
      .catch(function() { self.setState({ fetching: false }); });
  };

  handleChange = key => event => {
    const value = event.target.value;
    const self = this;
    this.setState(function(prev) {
      const nextForm = Object.assign({}, prev.form);
      nextForm[key] = value;
      // Clear the server-side "missing" flag for a field once it's edited.
      const nextMissing = prev.serverMissing.filter(function(k) {
        return k !== key;
      });
      return { form: nextForm, serverMissing: nextMissing };
    }, function() {
      // Auto-fetch details when a provider URL is entered/pasted, so "add the URL"
      // fills the fields without a click. Debounced, once per URL, blanks only.
      if (key === "source_url") {
        var url = String(value || "").trim();
        clearTimeout(self._autoFetchTimer);
        var f = self.state.form;
        var needsFill = !(f.title && String(f.title).trim()) || !(f.description && String(f.description).trim()) || !(f.credit_line && String(f.credit_line).trim());
        if (/^https?:\/\//i.test(url) && url !== self._autoFetchUrl && needsFill) {
          self._autoFetchTimer = setTimeout(function() { self._autoFetchUrl = url; self.handleFetchUrl(); }, 800);
        }
      }
    });
  };

  // Fields present on the analysis (optional ones only render when returned).
  visibleFields = () => {
    const fields =
      this.props.analysis && this.props.analysis.fields
        ? this.props.analysis.fields
        : {};
    return FIELD_DEFS.filter(function(def) {
      if (!def.optional) return true;
      return Object.prototype.hasOwnProperty.call(fields, def.key);
    });
  };

  missingMandatory = () => {
    const form = this.state.form;
    return MANDATORY.filter(function(k) {
      return isBlank(form[k]);
    });
  };

  saveDisabled = () => {
    return this.props.saving || this.missingMandatory().length > 0;
  };

  buildFields = () => {
    // Send back the full edited map. keywords -> array for the backend.
    const form = this.state.form;
    const out = {};
    Object.keys(form).forEach(function(k) {
      if (k === "keywords") {
        out[k] = form[k]
          .split(",")
          .map(function(s) {
            return s.trim();
          })
          .filter(Boolean);
      } else {
        out[k] = form[k];
      }
    });
    return out;
  };

  handleSave = (duplicateDecision, duplicateId) => () => {
    const payload = { fields: this.buildFields() };
    if (duplicateDecision) {
      payload.duplicateDecision = duplicateDecision;
      if (duplicateId) payload.duplicateId = duplicateId;
    }
    // onSave returns a promise; a rejected { mandatoryMissing } highlights fields.
    const self = this;
    Promise.resolve(this.props.onSave(payload)).catch(function(err) {
      if (err && err.mandatoryMissing) {
        self.setState({ serverMissing: err.mandatoryMissing });
      }
    });
  };

  renderDuplicates = () => {
    const duplicates =
      (this.props.analysis && this.props.analysis.duplicates) || [];
    if (!duplicates.length) return null;
    const top = duplicates[0];
    return (
      <div
        style={{
          border: "1px solid #ffcc80",
          background: "#fff8e1",
          borderRadius: 6,
          padding: 12,
          marginBottom: 16
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          Possible duplicate
          {duplicates.length > 1 ? "s" : ""} found
        </div>
        {duplicates.map(function(d) {
          return (
            <div
              key={d.publicId}
              style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
            >
              {d.thumb ? (
                <img
                  src={d.thumb}
                  alt=""
                  style={{
                    width: 48,
                    height: 48,
                    objectFit: "cover",
                    borderRadius: 4,
                    marginRight: 10
                  }}
                />
              ) : null}
              <div style={{ fontSize: 13 }}>
                <div style={{ fontWeight: 600 }}>{d.title || d.publicId}</div>
                <div style={{ color: "#888" }}>
                  {d.tier ? "tier " + d.tier : ""}
                  {typeof d.distance === "number"
                    ? " · distance " + d.distance
                    : ""}
                </div>
              </div>
            </div>
          );
        })}
        <div style={{ marginTop: 8 }}>
          <Button
            color="info"
            size="sm"
            onClick={this.handleSave("use_existing", top.publicId)}
            disabled={this.props.saving}
          >
            Use existing
          </Button>
          <Button
            color="rose"
            size="sm"
            onClick={this.handleSave("keep_both", top.publicId)}
            disabled={this.saveDisabled()}
          >
            Keep both
          </Button>
          <Button
            size="sm"
            onClick={this.handleSave("save_anyway", top.publicId)}
            disabled={this.saveDisabled()}
          >
            Save anyway
          </Button>
        </div>
      </div>
    );
  };

  render() {
    const classes = this.props.classes;
    const analysis = this.props.analysis || {};
    const serverMissing = this.state.serverMissing;
    const missing = this.missingMandatory();
    const self = this;

    return (
      <Dialog
        classes={{ root: classes.center, paper: classes.modal }}
        open={this.props.open}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ style: { maxHeight: "92vh", display: "flex", flexDirection: "column" } }}
        aria-labelledby="media-review-title"
      >
        <DialogTitle
          id="media-review-title"
          disableTypography
          className={classes.modalHeader}
        >
          <Button
            justIcon
            className={classes.modalCloseButton}
            key="close"
            aria-label="Close"
            color="transparent"
            onClick={this.props.onCancel}
          >
            <Close className={classes.modalClose} />
          </Button>
          <h4 className={classes.modalTitle}>Review &amp; Save</h4>
        </DialogTitle>

        <DialogContent
          id="media-review-body"
          className={classes.modalBody}
          style={{ overflowY: "auto", overflowX: "hidden", flex: "1 1 auto", minHeight: 0 }}
        >
          <Preview preview={analysis.preview} mediaType={analysis.mediaType} />

          {this.renderDuplicates()}

          <GridContainer>
            {this.visibleFields().map(function(def) {
              const flaggedByServer = serverMissing.indexOf(def.key) >= 0;
              const requiredEmpty =
                def.required && missing.indexOf(def.key) >= 0;
              const isError = flaggedByServer || requiredEmpty;
              return (
                <GridItem xs={12} md={def.area ? 12 : 6} key={def.key}>
                  <CustomInput
                    id={"media-" + def.key}
                    labelText={def.label + (def.required ? " *" : "")}
                    error={isError}
                    formControlProps={{ fullWidth: true }}
                    value={self.state.form[def.key]}
                    inputProps={{
                      value: self.state.form[def.key],
                      onChange: self.handleChange(def.key),
                      multiline: !!def.area,
                      rowsMax: def.area ? 6 : undefined
                    }}
                  />
                  {def.key === "source_url" ? (
                    <Button
                      size="sm"
                      color="rose"
                      simple
                      disabled={self.state.fetching || !(self.state.form.source_url || "").trim()}
                      onClick={self.handleFetchUrl}
                    >
                      {self.state.fetching ? "Fetching…" : "Fetch details from URL"}
                    </Button>
                  ) : null}
                </GridItem>
              );
            })}
          </GridContainer>

          {missing.length > 0 ? (
            <div style={{ color: "#f44336", fontSize: 13, marginTop: 8 }}>
              Required: {missing.join(", ")}
            </div>
          ) : null}
        </DialogContent>

        <DialogActions className={classes.modalFooter}>
          <Button onClick={this.props.onCancel} disabled={this.props.saving}>
            Cancel
          </Button>
          <Button
            color="rose"
            onClick={this.handleSave()}
            disabled={this.saveDisabled()}
          >
            {this.props.saving ? (
              <CircularProgress size={18} style={{ color: "#fff" }} />
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

const mapStateToProps = function(store) {
  return { activeDomain: store.login.user.activeDomain };
};

export default connect(mapStateToProps)(withStyles(modalStyle)(ReviewDialog));
